import { ecrireJson, lireJson, type Resultat } from './client';

/** Statut d'un compte rendu — même liste fermée que le serveur. */
export type StatutRapport = 'brouillon' | 'en-attente' | 'valide' | 'rejete';

/**
 * Une action autorisée sur un compte rendu, décidée par le SERVEUR.
 *
 * Le code sert à choisir une icône, le libellé est celui qu'on affiche. L'écran
 * ne déduit aucune action d'un statut, et n'écrit aucun libellé métier.
 */
export interface ActionRapport {
  readonly code: 'voir' | 'telecharger' | 'modifier' | 'valider' | 'rejeter' | 'supprimer';
  readonly libelle: string;
}

export interface Rapport {
  readonly id: string;
  /** L'intervention dont ce rapport rend compte. */
  readonly interventionId: string;
  readonly date: string;
  readonly dateLisible: string;
  readonly clientId: string;
  readonly client: string;
  readonly typeRapport: string;
  readonly lieu: string;
  readonly statut: StatutRapport;
  /** Dernière écriture, heure de Paris (`AAAA-MM-JJTHH:MM:SS`). */
  readonly modifieLe: string;
  /** « Aujourd'hui, 09:12 » — calculé par le serveur, pas par le navigateur. */
  readonly modifieLisible: string;
  readonly constat: string;
  readonly actionsRealisees: string | null;
  readonly anomalies: string | null;
  readonly avis: string | null;
  readonly avisLibelle: string | null;
  /** Pourquoi le rapport a été rejeté — affiché, jamais seulement en infobulle. */
  readonly motifRejet: string | null;
  /** Quand le rapport a été tranché (`AAAA-MM-JJTHH:MM:SS`, heure de Paris). */
  readonly controleLe: string | null;
  /** « Aujourd'hui, 10:51 » — calculé par le serveur. */
  readonly controleLisible: string | null;
  /** Qui a tranché (adresse du compte administrateur). */
  readonly controlePar: string | null;
  /** Le technicien de l'intervention (colonne « Assigné à »). */
  readonly technicien: string | null;
  /** « JD » — calculé par le serveur, jamais découpé ici. */
  readonly technicienInitiales: string | null;
  readonly actions: readonly ActionRapport[];
}

/** Une intervention terminée qui attend son compte rendu. */
export interface RapportVierge {
  /** Le technicien de l'intervention : c'est lui qu'on relance. */
  readonly technicienId: string | null;
  readonly technicien: string | null;
  readonly technicienInitiales: string | null;
  /** Dernière relance, `AAAA-MM-JJTHH:MM:SS` ; `null` = jamais relancé. */
  readonly relanceLe: string | null;
  /** « Relancé hier, 16:45 » — vide tant qu'aucune relance n'est partie. */
  readonly relanceLisible: string;
  readonly relances: number;
  /** Peut-on relancer MAINTENANT ? Décidé par le serveur (délai de 24 h compris). */
  readonly relancePossible: boolean;
  /** Ce que dit l'infobulle : « Envoyer un rappel à Jean Dupont », ou pourquoi pas. */
  readonly relanceLibelle: string;
  readonly interventionId: string;
  readonly date: string;
  readonly dateLisible: string;
  readonly clientId: string;
  readonly client: string;
  readonly lieu: string;
  readonly typeIntervention: string;
  readonly echeance: string;
  /** « En retard (2j) », « Dans 3 jours », « Dernier jour ». */
  readonly echeanceLisible: string;
  readonly enRetard: boolean;
  readonly joursRestants: number;
  readonly actionLibelle: string;
}

export interface CompteursRapports {
  readonly enAttente: number;
  readonly valides: number;
  readonly brouillons: number;
  readonly rejetes: number;
}

export interface CompteursVierges {
  readonly aRemplir: number;
  readonly enRetard: number;
  readonly total: number;
}

export interface PageRapports {
  readonly items: readonly Rapport[];
  readonly total: number;
  readonly page: number;
  readonly taille: number;
  readonly pages: number;
  readonly compteurs: CompteursRapports;
}

export interface PageVierges {
  readonly items: readonly RapportVierge[];
  readonly total: number;
  readonly page: number;
  readonly taille: number;
  readonly pages: number;
  readonly compteurs: CompteursVierges;
}

/** Listes du formulaire : elles viennent du serveur, jamais d'une copie locale. */
/** Période de consultation — liste FERMÉE, bornes calculées par le serveur. */
export type PeriodeRapport = 'semaine' | 'mois' | 'trimestre' | 'tout';

export interface ChoixPeriode {
  readonly code: PeriodeRapport;
  readonly libelle: string;
}

export interface ReferencesRapport {
  /** Le serveur dit si CE compte peut contrôler (valider, rejeter). */
  readonly controle: boolean;
  readonly statuts: ReadonlyArray<{ readonly code: StatutRapport; readonly libelle: string }>;
  readonly avis: ReadonlyArray<{ readonly code: string; readonly libelle: string }>;
  readonly tris: ReadonlyArray<{
    readonly code: string;
    readonly libelle: string;
    readonly sensDefaut: string;
  }>;
  readonly periodes: readonly ChoixPeriode[];
  readonly tailles: readonly number[];
  readonly constatMin: number;
  readonly texteMax: number;
  readonly delaiSaisieJours: number;
  readonly motifRejetMin: number;
  readonly motifRejetMax: number;
  readonly relanceMax: number;
  readonly relanceDelaiHeures: number;
}

export interface CriteresRapports {
  /** `semaine` · `mois` · `trimestre` · `tout` — le serveur en déduit les bornes. */
  readonly periode?: PeriodeRapport;
  readonly recherche?: string;
  readonly statut?: string;
  readonly tri?: string;
  readonly sens?: string;
  readonly page?: number;
  readonly taille?: number;
}

function requete(criteres: Record<string, string | number | boolean | undefined>): string {
  const parametres = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(criteres)) {
    if (valeur === undefined || valeur === false) continue;
    // Une recherche réduite à des blancs n'est pas un critère : l'envoyer
    // ferait filtrer sur trois espaces et rendrait une liste vide.
    const propre = typeof valeur === 'string' ? valeur.trim() : valeur;
    if (propre === '') continue;
    if (cle === 'page' && propre === 1) continue;
    parametres.set(cle, String(propre));
  }
  const suite = parametres.toString();
  return suite === '' ? '' : `?${suite}`;
}

export function urlRapports(criteres: CriteresRapports = {}): string {
  return `/api/rapports${requete({
    recherche: criteres.recherche,
    statut: criteres.statut,
    periode: criteres.periode,
    tri: criteres.tri,
    sens: criteres.sens,
    page: criteres.page,
    taille: criteres.taille,
  })}`;
}

/** File de contrôle : tous les portefeuilles (administration seulement). */
export function urlControle(criteres: CriteresRapports = {}): string {
  return `/api/admin/rapports${requete({
    recherche: criteres.recherche,
    statut: criteres.statut,
    periode: criteres.periode,
    tri: criteres.tri,
    sens: criteres.sens,
    page: criteres.page,
    taille: criteres.taille,
  })}`;
}

export function urlVierges(
  criteres: {
    recherche?: string;
    retard?: boolean;
    periode?: PeriodeRapport;
    page?: number;
    taille?: number;
  } = {},
): string {
  return `/api/rapports/vierges${requete({
    recherche: criteres.recherche,
    // `1` plutôt que `true` : l'adresse reste lisible et se partage telle quelle.
    retard: criteres.retard ? '1' : undefined,
    periode: criteres.periode,
    page: criteres.page,
    taille: criteres.taille,
  })}`;
}

/** L'export CSV de la liste filtrée : le serveur le fabrique. */
export function urlExport(criteres: CriteresRapports = {}): string {
  return `/api/rapports/export.csv${requete({
    recherche: criteres.recherche,
    statut: criteres.statut,
    tri: criteres.tri,
    sens: criteres.sens,
  })}`;
}

/** Le compte rendu en document imprimable (le navigateur en fait un PDF). */
export function urlDocument(id: string): string {
  return `/api/rapports/${encodeURIComponent(id)}/document`;
}

export function lireRapports(
  criteres: CriteresRapports = {},
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<PageRapports>> {
  return lireJson<PageRapports>(urlRapports(criteres), options);
}

export function lireControle(
  criteres: CriteresRapports = {},
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<PageRapports>> {
  return lireJson<PageRapports>(urlControle(criteres), options);
}

export function lireVierges(
  criteres: {
    recherche?: string;
    retard?: boolean;
    periode?: PeriodeRapport;
    page?: number;
    taille?: number;
  } = {},
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<PageVierges>> {
  return lireJson<PageVierges>(urlVierges(criteres), options);
}

export function lireReferencesRapports(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ReferencesRapport>> {
  return lireJson<ReferencesRapport>('/api/rapports/references', options);
}

export function lireRapport(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Rapport>> {
  return lireJson<Rapport>(`/api/rapports/${encodeURIComponent(id)}`, options);
}

/** Corps d'écriture : `camelCase`, comme les réponses de l'API. */
export interface SaisieRapport {
  readonly interventionId?: string;
  readonly constat: string;
  readonly actionsRealisees?: string;
  readonly anomalies?: string;
  readonly avis?: string;
  readonly soumettre?: boolean;
}

export function creerRapport(
  saisie: SaisieRapport,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Rapport>> {
  return ecrireJson<Rapport>('/api/rapports', 'POST', saisie, options);
}

export function enregistrerRapport(
  id: string,
  saisie: SaisieRapport,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Rapport>> {
  return ecrireJson<Rapport>(`/api/rapports/${encodeURIComponent(id)}`, 'PATCH', saisie, options);
}

/** Supprime un BROUILLON (204). Un rapport transmis laisse une trace. */
export function supprimerRapport(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<void>> {
  return ecrireJson<void>(`/api/rapports/${encodeURIComponent(id)}`, 'DELETE', undefined, options);
}

/** Contrôle qualité : valider (administration). */
export function validerRapport(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Rapport>> {
  return ecrireJson<Rapport>(
    `/api/admin/rapports/${encodeURIComponent(id)}/validation`,
    'POST',
    {},
    options,
  );
}

/** Contrôle qualité : rejeter, avec un motif OBLIGATOIRE. */
export function rejeterRapport(
  id: string,
  motif: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Rapport>> {
  return ecrireJson<Rapport>(
    `/api/admin/rapports/${encodeURIComponent(id)}/rejet`,
    'POST',
    { motif },
    options,
  );
}

/** Le sort d'un identifiant dans un contrôle groupé, décidé par le serveur. */
export interface ResultatControle {
  readonly id: string;
  readonly ok: boolean;
  readonly message: string;
}

export interface ReponseControles {
  readonly resultats: readonly ResultatControle[];
  readonly faits: number;
  readonly refuses: number;
}

/**
 * Contrôle GROUPÉ : une requête, un compte rendu par identifiant.
 *
 * L'écran n'enchaîne pas des appels unitaires — ce serait lui qui déciderait de
 * l'ordre et du compte rendu des refus.
 */
export function controlerLot(
  ids: readonly string[],
  decision: 'valider' | 'rejeter',
  motif?: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ReponseControles>> {
  return ecrireJson<ReponseControles>(
    '/api/admin/rapports/controles',
    'POST',
    { ids: [...ids], decision, ...(motif ? { motif } : {}) },
    options,
  );
}

/**
 * Relance le technicien d'UNE intervention terminée sans compte rendu.
 *
 * Aucun corps : le serveur décide seul (délai de 24 h, plafond de relances). Un
 * écran qui enverrait « oui » sans état ne vérifierait rien.
 */
export function relancerVierge(
  interventionId: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<RapportVierge>> {
  return ecrireJson<RapportVierge>(
    `/api/rapports/vierges/${encodeURIComponent(interventionId)}/relance`,
    'POST',
    {},
    options,
  );
}

/** Relance GROUPÉE : le serveur rend le sort de chaque intervention. */
export function relancerLot(
  ids: readonly string[],
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ReponseControles>> {
  return ecrireJson<ReponseControles>('/api/rapports/relances', 'POST', { ids: [...ids] }, options);
}
